/**
 * PhotoWallDisplayPage - Live slideshow display for TV/projector
 * Shows approved photos with transition effects and message overlays
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, QrCode, Wifi, WifiOff } from 'lucide-react';
import QRCode from 'react-qr-code';

import { API_URL } from '../utils/api';
const POLL_INTERVAL = 5000; // Check for new photos every 5 seconds
const SLIDE_DURATION = 8000; // Show each photo for 8 seconds

// Transition variants
const slideTransitions = {
  fadeSlide: {
    initial: { opacity: 0, x: 100 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -100 }
  },
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  },
  zoom: {
    initial: { opacity: 0, scale: 1.2 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 }
  },
  slideUp: {
    initial: { opacity: 0, y: 100 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -100 }
  }
};

export default function PhotoWallDisplayPage() {
  const { clubSlug } = useParams();
  const [session, setSession] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(true);
  const [transition, setTransition] = useState('fadeSlide');
  const [showQR, setShowQR] = useState(true);
  
  const lastPhotoIdRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const slideIntervalRef = useRef(null);

  // Fetch photos
  const fetchPhotos = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/clubs/${clubSlug}/photo-wall/photos/slideshow`);
      const data = await response.json();
      
      setConnected(true);
      
      if (data.active && data.session) {
        setSession(data.session);
        
        if (data.photos && data.photos.length > 0) {
          setPhotos(prevPhotos => {
            // Merge new photos, avoiding duplicates
            const existingIds = new Set(prevPhotos.map(p => p.photo_id));
            const newPhotos = data.photos.filter(p => !existingIds.has(p.photo_id));
            
            if (newPhotos.length > 0) {
              return [...prevPhotos, ...newPhotos];
            }
            return prevPhotos;
          });
          
          // Update last photo ID for next poll
          lastPhotoIdRef.current = data.photos[data.photos.length - 1]?.photo_id;
        }
      } else {
        // No active session
        setSession(null);
      }
    } catch (error) {
      console.error('Error fetching photos:', error);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  }, [clubSlug]);

  // Initial load
  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  // Poll for new photos
  useEffect(() => {
    pollIntervalRef.current = setInterval(fetchPhotos, POLL_INTERVAL);
    return () => clearInterval(pollIntervalRef.current);
  }, [fetchPhotos]);

  // Auto-advance slideshow
  useEffect(() => {
    if (photos.length > 1) {
      slideIntervalRef.current = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % photos.length);
        // Cycle through transitions
        const transitions = Object.keys(slideTransitions);
        setTransition(transitions[Math.floor(Math.random() * transitions.length)]);
      }, SLIDE_DURATION);
      
      return () => clearInterval(slideIntervalRef.current);
    }
  }, [photos.length]);

  // Toggle QR code visibility with keyboard
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'q' || e.key === 'Q') {
        setShowQR(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const currentPhoto = photos[currentIndex];
  const uploadUrl = `${window.location.origin}/club/${clubSlug}/photo-wall`;

  // Loading screen
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-20 h-20 rounded-full border-4 border-purple-500 border-t-transparent mx-auto mb-6"
          />
          <h1 className="text-2xl font-bold text-white">Loading Photo Wall...</h1>
        </div>
      </div>
    );
  }

  // No active session screen
  if (!session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-8">
        <div className="text-center max-w-xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-32 h-32 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-8"
          >
            <Camera className="w-16 h-16 text-slate-500" />
          </motion.div>
          <h1 className="text-4xl font-bold text-white mb-4">No Active Session</h1>
          <p className="text-xl text-purple-200 mb-8">
            The Photo Wall is not currently active for this venue.
          </p>
          <p className="text-slate-400">
            Ask the event host to start a Photo Wall session from their Dashboard.
          </p>
          {/* Connection Status */}
          <div className="mt-8 flex items-center justify-center gap-2">
            {connected ? (
              <>
                <Wifi className="w-5 h-5 text-green-400" />
                <span className="text-green-400 text-sm">Auto-refreshing every 5 seconds</span>
              </>
            ) : (
              <>
                <WifiOff className="w-5 h-5 text-red-400" />
                <span className="text-red-400">Connection lost</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // No photos yet screen
  if (photos.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-8">
        <div className="text-center max-w-2xl">
          {/* Session Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <h1 className="text-4xl font-bold text-white mb-2">{session.session_name}</h1>
            <p className="text-xl text-purple-300">{session.club_name}</p>
          </motion.div>

          {/* Waiting Animation */}
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="w-32 h-32 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mx-auto mb-8"
          >
            <Camera className="w-16 h-16 text-white" />
          </motion.div>

          <h2 className="text-3xl font-semibold text-white mb-4">
            Waiting for photos...
          </h2>
          <p className="text-xl text-purple-200 mb-12">
            Scan the QR code to share your photos!
          </p>

          {/* QR Code */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white p-6 rounded-2xl inline-block shadow-2xl"
          >
            <QRCode value={uploadUrl} size={200} />
            <p className="text-sm text-slate-600 mt-4 font-medium">Scan to upload photos</p>
          </motion.div>

          {/* Connection Status */}
          <div className="mt-8 flex items-center justify-center gap-2">
            {connected ? (
              <>
                <Wifi className="w-5 h-5 text-green-400" />
                <span className="text-green-400">Connected</span>
              </>
            ) : (
              <>
                <WifiOff className="w-5 h-5 text-red-400" />
                <span className="text-red-400">Reconnecting...</span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Slideshow display
  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      {/* Photo Display */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentPhoto.photo_id}
          variants={slideTransitions[transition]}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.8, ease: "easeInOut" }}
          className="absolute inset-0"
        >
          {/* Background blur */}
          <div 
            className="absolute inset-0 bg-cover bg-center blur-3xl opacity-30"
            style={{ backgroundImage: `url(${currentPhoto.photo_url})` }}
          />
          
          {/* Main image */}
          <div className="absolute inset-0 flex items-center justify-center p-8">
            <img
              src={currentPhoto.photo_url}
              alt={`Photo by ${currentPhoto.uploader_name}`}
              className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
            />
          </div>

          {/* Message overlay */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-8"
          >
            <div className="max-w-4xl mx-auto">
              <p className="text-2xl font-bold text-white mb-2">
                {currentPhoto.uploader_name}
              </p>
              {currentPhoto.message && (
                <p className="text-xl text-white/80 italic">
                  "{currentPhoto.message}"
                </p>
              )}
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* Session info header */}
      <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 to-transparent p-6">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          <div>
            <h1 className="text-2xl font-bold text-white">{session.session_name}</h1>
            <p className="text-white/70">{session.club_name}</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/70 text-sm">
              {currentIndex + 1} / {photos.length} photos
            </span>
            {connected ? (
              <Wifi className="w-5 h-5 text-green-400" />
            ) : (
              <WifiOff className="w-5 h-5 text-red-400" />
            )}
          </div>
        </div>
      </div>

      {/* QR Code overlay (toggle with Q key) */}
      <AnimatePresence>
        {showQR && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="absolute bottom-8 right-8 bg-white p-4 rounded-xl shadow-2xl"
          >
            <QRCode value={uploadUrl} size={120} />
            <p className="text-xs text-center text-slate-600 mt-2">Scan to share</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20">
        <motion.div
          key={currentPhoto.photo_id}
          initial={{ width: 0 }}
          animate={{ width: '100%' }}
          transition={{ duration: SLIDE_DURATION / 1000, ease: "linear" }}
          className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
        />
      </div>
    </div>
  );
}
